/**
 * A prescription is the set of medicines written at one consultation.
 *
 * The database models it that way — `prescription_items` keyed by
 * `(visit_id, position)` — but every list that reads it back is flat, so
 * grouping has to be put back on the way out. Doing it by date alone would be
 * wrong: nothing stops a patient being seen twice in one day, and that would
 * merge two prescriptions into one.
 */

export interface Prescribed {
  visitId: string
  drug_name: string
  dose: string | null
  frequency: string | null
  duration: string | null
  instructions: string | null
}

export interface PrescriptionGroup<T> {
  visitId: string
  items: T[]
}

/**
 * Consecutive runs sharing a visit, in the order given.
 *
 * Deliberately not a map keyed by visit: the caller has already sorted these
 * (newest consultation first), and rebuilding from a map would either lose that
 * order or silently reunite two runs that the sort had separated.
 */
export function groupByVisit<T>(rows: T[], visitIdOf: (row: T) => string): PrescriptionGroup<T>[] {
  const groups: PrescriptionGroup<T>[] = []
  for (const row of rows) {
    const visitId = visitIdOf(row)
    const last = groups[groups.length - 1]
    if (last && last.visitId === visitId) last.items.push(row)
    else groups.push({ visitId, items: [row] })
  }
  return groups
}
